import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Globe, Phone, MapPin, Facebook, Instagram, Twitter, MessageCircle, Tag, Trash2, Package, Wrench, Clock, Lock, Eye, Mail, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import StarRating from "@/components/StarRating";
import ContactProductModal from "@/components/ContactProductModal";
import StoryRail from "@/components/StoryRail";

const ProfileDetail = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const autoOpenStoryId = searchParams.get("story");
  const [biz, setBiz] = useState(null);
  const [error, setError] = useState("");
  const [ratings, setRatings] = useState({ items: [], avg_rating: 0, ratings_count: 0 });
  const [myStars, setMyStars] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [savingRating, setSavingRating] = useState(false);
  const [ratingErr, setRatingErr] = useState("");
  const [products, setProducts] = useState([]);
  const [contactProduct, setContactProduct] = useState(null);

  const loadRatings = useCallback(async () => {
    try {
      const { data } = await api.get(`/entrepreneurs/${id}/ratings`);
      setRatings(data);
    } catch (err) { console.error("Failed to load ratings:", err); }
  }, [id]);

  const loadMyRating = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const { data } = await api.get(`/entrepreneurs/${id}/my-rating`);
      setMyStars(data.stars || 0);
      setMyComment(data.comment || "");
    } catch (err) { console.error("Failed to load my rating:", err); }
  }, [id, user]);

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await api.get(`/products/entrepreneur/${id}`);
      setProducts(data.items || []);
    } catch (err) { console.error("Failed to load products:", err); }
  }, [id]);

  useEffect(() => {
    if (user === null) return; // wait for auth check to resolve
    api.get(`/entrepreneurs/${id}`)
      .then(({ data }) => {
        setBiz(data);
        // Only fetch products for entrepreneur profiles (Professionals have no catalog)
        if ((data.profile_type || "entrepreneur") === "entrepreneur") {
          loadProducts();
        } else {
          setProducts([]);
        }
      })
      .catch((e) => setError(formatApiError(e)));
    loadRatings();
    loadMyRating();
  }, [id, user, loadRatings, loadMyRating, loadProducts]);

  if (user === null) return <div className="container-tight py-24 text-center text-teal-soft">…</div>;
  if (error) return <div className="container-tight py-24 text-center text-red-600">{error}</div>;
  if (!biz) return <div className="container-tight py-24 text-center text-teal-soft">Loading…</div>;

  const trackClick = (kind) => {
    if (!user) return;
    api.post(`/entrepreneurs/${id}/contact-click?kind=${encodeURIComponent(kind)}`).catch(() => {});
  };

  const isOwner = !!user && biz.user_id === user.id;
  const canRate = !!user && user.role !== "admin" && !isOwner;

  const submitRating = async (e) => {
    e.preventDefault();
    if (myStars < 1) { setRatingErr(t.ratings.pickStars); return; }
    setSavingRating(true); setRatingErr("");
    try {
      await api.post(`/entrepreneurs/${id}/rate`, { stars: myStars, comment: myComment });
      await loadRatings();
    } catch (err) { setRatingErr(formatApiError(err)); }
    finally { setSavingRating(false); }
  };

  const deleteMyRating = async () => {
    if (!window.confirm(t.ratings.confirmDelete)) return;
    try {
      await api.delete(`/entrepreneurs/${id}/rate`);
      setMyStars(0); setMyComment("");
      await loadRatings();
    } catch (err) { setRatingErr(formatApiError(err)); }
  };

  const cat = t.categories[biz.category] || biz.category;
  const socials = [
    { k: "website", url: biz.website, Icon: Globe, label: biz.website },
    { k: "phone", url: biz.phone ? `tel:${biz.phone}` : "", Icon: Phone, label: biz.phone },
    { k: "whatsapp", url: biz.whatsapp ? `https://wa.me/${biz.whatsapp.replace(/\D/g, '')}` : "", Icon: MessageCircle, label: biz.whatsapp },
    { k: "email", url: biz.email ? `mailto:${biz.email}` : "", Icon: Mail, label: biz.email },
    { k: "facebook", url: biz.facebook && (biz.facebook.startsWith("http") ? biz.facebook : `https://facebook.com/${biz.facebook}`), Icon: Facebook, label: biz.facebook },
    { k: "instagram", url: biz.instagram && (biz.instagram.startsWith("http") ? biz.instagram : `https://instagram.com/${biz.instagram.replace('@','')}`), Icon: Instagram, label: biz.instagram },
    { k: "twitter", url: biz.twitter && (biz.twitter.startsWith("http") ? biz.twitter : `https://twitter.com/${biz.twitter.replace('@','')}`), Icon: Twitter, label: biz.twitter },
  ].filter((s) => s.label);

  return (
    <article className="bg-cream pb-20 min-h-screen">
      {/* Facebook-style profile header — cover + overlapping avatar */}
      <div className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto">
          {/* Cover photo (Facebook ratio ~2.7:1) */}
          <div className="relative w-full aspect-[2.7/1] overflow-hidden md:rounded-b-2xl">
            {biz.cover_url ? (
              <img
                src={biz.cover_url}
                alt=""
                className="w-full h-full object-cover"
                style={{ objectPosition: `center ${biz.cover_position ?? 50}%` }}
                data-testid="profile-cover"
              />
            ) : (
              <div className="relative w-full h-full bg-gradient-to-br from-teal-deep via-teal to-orange/60 flex items-center justify-center overflow-hidden" data-testid="profile-cover-empty">
                <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-white/10 blur-3xl" aria-hidden />
                <div className="absolute -bottom-10 -left-10 w-52 h-52 rounded-full bg-black/10 blur-3xl" aria-hidden />
                <div className="absolute inset-0 bg-black/25" aria-hidden />
                <span className="relative z-10 font-display text-white tracking-wide text-3xl md:text-5xl px-8 text-center drop-shadow-md">
                  {biz.business_name}
                </span>
              </div>
            )}
            <Link
              to="/directory"
              className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 bg-black/40 backdrop-blur-sm text-white hover:text-orange rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
              data-testid="back-to-directory"
            >
              <ArrowLeft size={14} /> {t.nav.directory}
            </Link>
          </div>

          {/* Identity row: avatar overlaps cover; name + CTAs stay below the cover line */}
          <div className="px-4 md:px-8 pb-6 pt-4 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {/* Avatar — self-lifted to overlap the cover, but its box stays inline */}
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-white shadow-xl bg-teal-mist shrink-0 relative z-10 -mt-20 md:-mt-24 md:mb-2">
                {biz.logo_url ? (
                  <img
                    src={biz.logo_url}
                    alt={biz.business_name}
                    className="w-full h-full object-cover"
                    data-testid="profile-avatar"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display text-5xl text-teal bg-teal-mist">
                    {biz.business_name.slice(0, 1)}
                  </div>
                )}
              </div>

              {/* Name + meta — sits below the cover on desktop */}
              <div className="flex-1 min-w-0 md:pl-2">
                <h1
                  className="font-display text-3xl md:text-4xl text-teal-deep leading-tight break-words"
                  data-testid="profile-title"
                >
                  {biz.business_name}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-orange/10 text-orange rounded-full px-3 py-1">
                    <Tag size={11} /> {cat}
                  </span>
                  {biz.city && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-teal/10 text-teal rounded-full px-3 py-1">
                      <MapPin size={11} /> {biz.city}
                      {biz.state ? `, ${biz.state}` : ""}
                    </span>
                  )}
                  {biz.owner_name && (
                    <span className="text-sm text-teal-soft">· {biz.owner_name}</span>
                  )}
                </div>
                {ratings.ratings_count > 0 && (
                  <div className="mt-2" data-testid="profile-rating-summary">
                    <StarRating
                      value={ratings.avg_rating}
                      count={ratings.ratings_count}
                      showValue
                      size={16}
                    />
                  </div>
                )}
              </div>

              {/* Action buttons — Facebook-style right column */}
              <div className="flex flex-wrap gap-2 md:pb-2 md:justify-end">
                {isOwner && biz.profile_id && (
                  <Link
                    to={`/profiles/edit/${biz.profile_id}`}
                    className="btn-orange"
                    data-testid="edit-profile-btn"
                  >
                    <Pencil size={14} /> {t.community.editProfile}
                  </Link>
                )}
                {!isOwner && biz.whatsapp && (
                  <a
                    href={`https://wa.me/${biz.whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackClick("whatsapp")}
                    className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb457] text-white text-xs font-bold uppercase tracking-wider rounded-[10px] px-5 py-3 transition-colors"
                    data-testid="cta-whatsapp"
                  >
                    <MessageCircle size={15} /> {t.community.ctaWhatsapp}
                  </a>
                )}
                {!isOwner && biz.phone && (
                  <a
                    href={`tel:${biz.phone}`}
                    onClick={() => trackClick("phone")}
                    className="btn-teal-outline"
                    data-testid="cta-call"
                  >
                    <Phone size={14} /> {t.community.ctaCall}
                  </a>
                )}
                {!isOwner && biz.website && (
                  <a
                    href={biz.website}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackClick("website")}
                    className="btn-teal-outline"
                    data-testid="cta-website"
                  >
                    <Globe size={14} /> {t.community.ctaWebsite}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content area (unchanged grid) */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 mt-6">
        {/* Story rail for this profile — renders nothing when no stories + not owner */}
        <StoryRail
          mode="profile"
          entrepreneurId={biz.id}
          profileId={biz.profile_id || null}
          ownerProfile={isOwner && biz.profile_id ? { id: biz.profile_id, name: biz.business_name } : null}
          currentUserId={user?.id || null}
          autoOpenStoryId={autoOpenStoryId}
        />
        <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Description — redesigned */}
            <div className="lg:col-span-2">
              <h2 className="eyebrow text-orange">{t.community.aboutLabel}</h2>
              <div className="mt-4 bg-cream/70 border-l-4 border-orange rounded-r-2xl p-6 md:p-8" data-testid="profile-description">
                <p className="text-teal-deep text-base md:text-lg leading-relaxed whitespace-pre-line">{biz.description}</p>
              </div>
              {(biz.view_count || 0) > 0 && (
                <p className="mt-4 inline-flex items-center gap-2 text-xs text-teal-soft">
                  <Eye size={13} /> {biz.view_count} {t.community.viewsLabel}
                </p>
              )}
            </div>

            {/* Contact — redesigned card, members-only data */}
            <aside className="rounded-2xl border border-gray-200 overflow-hidden h-fit shadow-sm bg-white">
              <div className="bg-teal-deep px-6 py-4">
                <h3 className="text-white font-display text-lg leading-none">{t.community.contactLabel}</h3>
              </div>
              <div className="p-5">
                {biz.contact_locked ? (
                  <div className="text-center py-3" data-testid="contact-locked">
                    <span className="w-12 h-12 rounded-2xl bg-orange/10 text-orange mx-auto flex items-center justify-center">
                      <Lock size={20} />
                    </span>
                    <h4 className="font-display text-lg text-teal-deep mt-3 leading-tight">{t.community.contactLockedTitle}</h4>
                    <p className="text-xs text-teal-soft mt-2 leading-relaxed">{t.community.contactLockedSub}</p>
                    <Link to="/user" className="btn-orange w-full justify-center mt-5" data-testid="contact-locked-join">
                      {t.community.contactLockedCta}
                    </Link>
                    <Link to="/login" className="block text-xs font-bold text-teal hover:text-orange mt-3 transition-colors" data-testid="contact-locked-login">
                      {t.community.contactLockedLogin}
                    </Link>
                  </div>
                ) : (
                  <>
                    <ul className="space-y-2">
                      {socials.map(({ k, url, Icon, label }) => (
                        <li key={k}>
                          {url ? (
                            <a href={url} target="_blank" rel="noreferrer" onClick={() => trackClick(k)} className="flex items-center gap-3 bg-cream rounded-xl px-3 py-2.5 text-teal hover:bg-teal-mist hover:text-teal-deep transition-colors break-all" data-testid={`profile-${k}`}>
                              <span className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0"><Icon size={15} /></span>
                              <span className="text-sm font-medium">{label}</span>
                            </a>
                          ) : (
                            <span className="flex items-center gap-3 bg-cream rounded-xl px-3 py-2.5 text-teal-soft break-all">
                              <span className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0"><Icon size={15} /></span>
                              <span className="text-sm">{label}</span>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {biz.whatsapp_locked && (
                      <div className="mt-4 flex items-center gap-2 text-xs text-teal-soft bg-cream border border-dashed border-gray-300 rounded-xl px-3 py-2.5" data-testid="whatsapp-locked">
                        <Lock size={12} className="shrink-0 text-orange" />
                        {t.community.whatsappLocked}
                      </div>
                    )}
                  </>
                )}
              </div>
            </aside>
          </div>

          {/* Catalog (public showcase) */}
          {products.length > 0 && (
            <div className="mt-12 pt-10 border-t border-gray-100" data-testid="profile-catalog">
              <p className="eyebrow text-orange">{t.catalog.publicTitle}</p>
              <h2 className="font-display text-3xl text-teal-deep mt-1 leading-tight">{t.catalog.publicTitle}</h2>
              <p className="text-sm text-teal-soft mt-2 max-w-xl">{t.catalog.publicSubtitle}</p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {products.map((p) => {
                  let money = null;
                  if (p.price != null && p.price !== "") {
                    try {
                      money = new Intl.NumberFormat("en-US", { style: "currency", currency: p.currency || "USD", maximumFractionDigits: 2 }).format(p.price);
                    } catch { money = `${p.currency || ""} ${Number(p.price).toFixed(2)}`; }
                  }
                  return (
                    <div key={p.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col group" data-testid={`profile-product-${p.id}`}>
                      <div className="aspect-square bg-cream flex items-center justify-center overflow-hidden relative">
                        {p.image
                          ? <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          : (Array.isArray(p.gallery) && p.gallery[0]
                            ? <img src={p.gallery[0]} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            : (p.type === "service" ? <Wrench size={32} className="text-teal-soft/40" /> : <Tag size={32} className="text-teal-soft/40" />))}
                        <span className={`absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${p.type === "service" ? "bg-teal text-white" : "bg-orange text-white"}`}>
                          {p.type === "service" ? <Wrench size={10} /> : <Package size={10} />}
                          {p.type === "service" ? t.catalog.serviceLabel : t.catalog.productLabel}
                        </span>
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <h3 className="font-display text-lg text-teal-deep leading-tight">{p.title}</h3>
                        {p.category && <span className="text-xs text-teal-soft mt-1">{p.category}</span>}
                        {p.duration && (
                          <span className="inline-flex items-center gap-1 text-xs text-teal-soft mt-1">
                            <Clock size={11} /> {p.duration}
                          </span>
                        )}
                        {p.description && <p className="text-sm text-teal-soft mt-2 line-clamp-2">{p.description}</p>}
                        {Array.isArray(p.gallery) && p.gallery.length > 0 && (
                          <div className="mt-3 flex gap-1.5 flex-wrap" data-testid={`product-gallery-${p.id}`}>
                            {p.gallery.slice(0, 6).map((g, gi) => (
                              <a
                                key={g}
                                href={g}
                                target="_blank"
                                rel="noreferrer"
                                className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 bg-cream hover:ring-2 hover:ring-orange transition-all"
                                data-testid={`product-gallery-thumb-${p.id}-${gi}`}
                              >
                                <img src={g} alt="" className="w-full h-full object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                        <div className="mt-3">
                          <span className="font-display text-xl text-orange">
                            {money ?? <span className="text-teal-soft text-sm italic">{t.catalog.askPrice}</span>}
                          </span>
                        </div>
                        {user && !isOwner && (
                          <button
                            type="button"
                            onClick={() => setContactProduct(p)}
                            className="mt-4 btn-orange w-full justify-center"
                            data-testid={`product-contact-${p.id}`}
                          >
                            <MessageCircle size={14} /> {t.catalog.contactBtn}
                          </button>
                        )}
                        {!user && (
                          <Link to="/user" className="mt-4 btn-teal-outline w-full justify-center" data-testid={`product-join-${p.id}`}>
                            <Lock size={13} /> {t.community.contactLockedCta}
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ratings section */}
          <div className="mt-12 pt-10 border-t border-gray-100" data-testid="ratings-section">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
              <div>
                <h2 className="eyebrow text-orange">{t.ratings.title}</h2>
                <h3 className="font-display text-3xl text-teal-deep mt-1 leading-tight">
                  {ratings.ratings_count > 0
                    ? <>{t.ratings.avgLabel}: <span className="text-orange">{ratings.avg_rating.toFixed(1)}</span> <span className="text-base text-teal-soft font-normal">· {ratings.ratings_count} {ratings.ratings_count === 1 ? t.ratings.reviewSingular : t.ratings.reviewPlural}</span></>
                    : t.ratings.noneTitle}
                </h3>
                {ratings.ratings_count === 0 && <p className="text-teal-soft text-sm mt-1">{t.ratings.noneSub}</p>}
              </div>
            </div>

            {/* Rate form */}
            {canRate && (
              <form onSubmit={submitRating} className="mt-6 bg-cream rounded-2xl p-5 border border-gray-200" data-testid="rate-form">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-sm font-bold text-teal-deep">{myStars > 0 ? t.ratings.yourRating : t.ratings.leaveRating}:</span>
                  <StarRating value={myStars} onChange={setMyStars} size={28} testid="rate" />
                </div>
                <textarea
                  rows={2}
                  className="field-input mt-3"
                  placeholder={t.ratings.commentPlaceholder}
                  value={myComment}
                  onChange={(e) => setMyComment(e.target.value)}
                  maxLength={500}
                  data-testid="rate-comment"
                />
                {ratingErr && <p className="text-red-600 text-sm mt-2">{ratingErr}</p>}
                <div className="flex items-center gap-3 mt-3">
                  <button type="submit" disabled={savingRating} className="btn-orange" data-testid="rate-submit">
                    {savingRating ? "…" : t.ratings.submit}
                  </button>
                  {myStars > 0 && (
                    <button type="button" onClick={deleteMyRating} className="text-sm text-red-500 hover:text-red-700 inline-flex items-center gap-1" data-testid="rate-delete">
                      <Trash2 size={14} /> {t.ratings.removeMine}
                    </button>
                  )}
                </div>
              </form>
            )}
            {isOwner && <p className="mt-4 text-sm text-teal-soft italic">{t.ratings.ownProfileNote}</p>}

            {/* Reviews list */}
            {ratings.items.length > 0 && (
              <div className="mt-8 space-y-4">
                {ratings.items.map((r) => (
                  <div key={r.id} className="bg-white border border-gray-200 rounded-2xl p-5">
                    <div className="flex items-center gap-3 flex-wrap">
                      <StarRating value={r.stars} size={14} />
                      <span className="text-sm font-bold text-teal-deep">{r.user_name || t.ratings.anonymous}</span>
                      <span className="text-xs text-teal-soft ml-auto">{(r.created_at || "").slice(0, 10)}</span>
                    </div>
                    {r.comment && <p className="text-sm text-teal-soft mt-2 leading-relaxed">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {contactProduct && (
        <ContactProductModal
          product={contactProduct}
          entrepreneur={biz}
          onClose={() => setContactProduct(null)}
        />
      )}
    </article>
  );
};

export default ProfileDetail;
